import { until } from "@vueuse/core";

import { snakeCase } from "lodash";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { RouteParamsGeneric, useRoute } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useWorktreeStore } from "@/composables/worktree-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { type types, useServices } from "@/lib/openapi";
import { EntityType as EntityTypeEnum } from "@/lib/openapi/gen/types.gen";

import { useSteksManager } from "./steks-store";
import { SyncReturn, useSyncStore } from "./sync-store";

export type EntityInfo = types.EntityInfo;
export type EntityType = types.EntityType;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const EntityType = EntityTypeEnum;
export type ProductInTree = types.ProductInTree;
export type ProcessInTree = types.ProcessInTree;

/**
 * Check if is valid EntityType key.
 */
function isEntityType(value: string): value is keyof typeof EntityType {
    return Object.keys(EntityType).includes(value);
}

/**
 * Convert a string into an EntityType.
 */
function getEntityType(value: string): EntityType {
    if (isEntityType(value)) {
        return EntityType[value];
    }
    throw new Error(`Invalid EntityType: ${value}`);
}

/**
 * Gets the current entityId from the router.
 */
export function getEntityInfo(params: RouteParamsGeneric) {
    const entityIds = Object.entries(params).filter(
        ([key, value]) => key.endsWith("Id") && value
    );

    const entityTypes = Object.entries(params).filter(
        ([key, value]) => key.endsWith("Type") && value
    );

    if (entityIds.length === 0) {
        // Check for Stek
        if (params.stekName) {
            const { steks } = useSteksManager();
            const entityType = getEntityType("STEK");
            const entityId = steks.value?.find(
                (stek) =>
                    stek.name === params.stekName ||
                    stek.identity === params.stekName
            )?.identity;
            return { entityId, entityType };
        } else {
            return;
        }
    }
    const [entityIdName, _entityId] = entityIds[entityIds.length - 1];

    let entityTypeKey: string | undefined;
    if (entityTypes.length === 0) {
        entityTypeKey = entityIdName.replace("Id", "");
    } else {
        entityTypeKey = `${entityTypes[entityTypes.length - 1][1]}`;
    }
    const entityId = `${_entityId}`;
    const entityType = getEntityType(snakeCase(entityTypeKey).toUpperCase());

    return { entityId, entityType };
}

export const useEntityStore = (
    entityType?: EntityType,
    entityId?: string,
    isCurrentEntity: boolean = false
) => {
    // Create a unique store ID for this entity
    const storeId = entityId ? `entity-${entityId}` : "entity";

    return defineStore(storeId, () => {
        const entity = ref<EntityInfo | null>();
        const isFetching = ref(false);
        const api = useServices({
            requires: {
                auth: true,
                organization: true,
                stek: false,
            },
        });
        const toast = useToast();
        const { findDomainByIdentity, findProductByIdentity } =
            useWorktreeStore();

        /**
         * If the store is for the current entity.
         */
        if (isCurrentEntity) {
            const route = useRoute();
            const { organization } = useOrganizationManager();

            // Helper to attempt fetching entity based on current route
            const tryFetchFromRoute = async () => {
                // Guard: only fetch if organization is available
                // This prevents race conditions on first login where entity
                // fetch is attempted before organization is selected
                if (!organization.value?.identity) {
                    entity.value = undefined;
                    return;
                }

                // Snapshot the org ID before any async operations.
                // The `until(steks)` wait below can take several seconds on slow connections,
                // during which the active organization may change. If it does, bail out —
                // the organization-change watcher will re-trigger this function with the new org.
                const orgIdAtStart = organization.value.identity;

                if (route.params.stekName) {
                    const { steks } = useSteksManager();
                    // Somehow `toBeUndefined` isn't available for this `until` call.
                    await until(steks).toMatch(
                        (_steks) => _steks !== undefined
                    );
                }

                // Bail out if the organization changed while we were waiting for steks.
                if (organization.value?.identity !== orgIdAtStart) {
                    return;
                }

                const entityInfo = getEntityInfo(route.params);

                if (!entityInfo?.entityId) {
                    entity.value = undefined;
                    return;
                }

                isFetching.value = true;

                entityId = entityInfo.entityId;
                entityType = entityInfo.entityType;

                try {
                    await fetchEntity();
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Request was aborted by the AbortController.
                        return;
                    } else {
                        // Don't show toast here as it's already shown in getEntity
                        console.error("Error fetching entity info:", e);
                    }
                } finally {
                    isFetching.value = false;
                }
            };

            // Watch route params changes
            watch(
                () => route.params,
                async (newParams) => {
                    if (newParams) {
                        await tryFetchFromRoute();
                    }
                },
                { immediate: true }
            );

            // Watch for organization changes to retry fetching
            // This handles the case where entity fetch was skipped because
            // organization wasn't available yet
            watch(
                () => organization.value?.identity,
                async (orgId) => {
                    if (orgId && route.params && !entity.value) {
                        await tryFetchFromRoute();
                    }
                }
            );
        }

        const controller = ref(new AbortController());

        let lastFetch: string;
        let lastResult: EntityInfo | undefined;

        /**
         * Get the entity for the current store.
         */
        const fetchEntity = async (force: boolean = false) => {
            // Avoid fetching again if the same entity is already fetched. This prevents double fetches upon loading
            // the router the very first time.
            if (!force && lastFetch === `${entityId}-${entityType}`) {
                entity.value = lastResult;
            } else if (!entityId || !entityType) {
                entity.value = undefined;
            } else {
                isFetching.value = true;

                const path = {
                    entity_id: `${entityId}`,
                } as types.StartBizEntitiesEntityTypeEntityIdInfoGetData["path"];
                if (entityType) {
                    path.entity_type = entityType;
                }

                // Abort any previously running requests and create a new controller
                controller.value.abort();
                controller.value = new AbortController();

                const handleEntityError = (error: unknown) => {
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : typeof error === "string"
                              ? error
                              : "Failed to get entity info";

                    // Don't show toast for organization not available errors -
                    // this is a race condition that will resolve when org is selected
                    if (errorMessage.includes("organization not available")) {
                        console.debug(
                            "Entity fetch skipped - waiting for organization"
                        );
                        return;
                    }

                    toast.add({
                        severity: "error",
                        summary: "Entity Info Error",
                        detail: `Failed to load entity information: ${errorMessage}`,
                    });
                    console.error("Error fetching entity info:", error);
                };

                try {
                    const { response, data: _entity } =
                        await api.startBizEntitiesEntityTypeEntityIdInfoGet({
                            path,
                            signal: controller.value.signal,
                            throwOnError: false,
                        });

                    if (!response) {
                        // Request was aborted or failed before getting a response.
                        // This is not an error - just silently return.
                        entity.value = undefined;
                        lastFetch = "";
                    } else if (!response.ok) {
                        if (response.status === 404) {
                            // Entity not found — it may have just been created and
                            // not yet available. Silently fail; the info will be
                            // fetched again on next access.
                            entity.value = undefined;
                            lastFetch = "";
                        } else {
                            handleEntityError(
                                response.statusText ||
                                    "Failed to get entity info"
                            );
                            entity.value = undefined;
                            lastFetch = "";
                        }
                    } else {
                        lastFetch = `${entityId}-${entityType}`;
                        lastResult = _entity;
                        entity.value = _entity;
                    }
                } catch (e) {
                    // Reset lastFetch so we retry on next call
                    lastFetch = "";
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Request was aborted by the AbortController.
                        return;
                    } else {
                        handleEntityError(e);
                        throw e;
                    }
                } finally {
                    isFetching.value = false;
                }
            }
        };

        // Find a specific entity in the ancestors.
        const findType = ({
            entityType,
        }: {
            entityType: EntityType;
        }): EntityInfo | undefined => {
            if (!entity.value) {
                return;
            }

            let parent: EntityInfo | null | undefined = entity.value;
            while (parent) {
                if (parent.entity_type === entityType) {
                    return parent;
                }
                parent = parent.parent;
            }
            return;
        };

        const computedEntity = ({ entityType }: { entityType: EntityType }) => {
            return computed(() => findType({ entityType }));
        };

        // Add some entity information to the domain - later on we can return
        // this kind of stuff from the new info endpoint.
        interface DomainEntity extends EntityInfo {
            products: ProductInTree[];
        }
        const domain = computed<DomainEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const domain = findType({ entityType: EntityType.DOMAIN });
            if (!domain) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'products' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'products' store and we do not want to have to do
            // another request.
            const domainInWorktree = findDomainByIdentity(domain.identity);
            return {
                ...domain,
                products: domainInWorktree ? domainInWorktree.products : [],
            } as DomainEntity;
        });

        // Add some entity information to the product - later on we can return
        // this kind of stuff from the new info endpoint.
        interface ProductEntity extends EntityInfo {
            processes: ProcessInTree[];
        }
        const product = computed<ProductEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const product = findType({ entityType: EntityType.PRODUCT });
            if (!product) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'processes' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'processes' store and we do not want to have to
            // do another request.
            const productInWorktree = findProductByIdentity(product.identity);
            return {
                ...product,
                processes: productInWorktree ? productInWorktree.processes : [],
            };
        });

        const findAncestorById = (identity: string): EntityInfo | undefined => {
            let currentEntity = entity.value;
            while (currentEntity) {
                if (identity === currentEntity.identity) {
                    return currentEntity;
                }
                currentEntity = currentEntity.parent;
            }
        };

        onMounted(async () => {
            // Actually set the Entity.
            await fetchEntity();

            syncChannel.addEventListener(
                "message",
                async ({ data: { message, identity } }) => {
                    if (
                        message === "synced" &&
                        ["name", "description", "process_definition"].includes(
                            identity.attribute
                        ) &&
                        findAncestorById(identity.entityId)
                    ) {
                        await fetchEntity(true);
                    }
                }
            );

            useSyncStore().$onAction(({ name, after }) => {
                if (name === "sync") {
                    after(async (fullySyncedIdentities: SyncReturn) => {
                        if (
                            fullySyncedIdentities.some(
                                (identity) =>
                                    [
                                        "description",
                                        "name",
                                        "process_definition",
                                    ].includes(identity.attribute) &&
                                    findAncestorById(identity.entityId)
                            )
                        ) {
                            await fetchEntity(true);
                        }
                    });
                }
            });
        });

        return {
            entity,
            identity: computed(() => entity.value?.identity || "unknown"),
            name: computed(() => entity.value?.name || "unknown"),
            entityType: computed(() => entity.value?.entity_type || "unknown"),
            businessEntity: computedEntity({
                entityType: EntityType.BUSINESS_ENTITY,
            }),
            domain,
            product,
            role: computedEntity({ entityType: EntityType.ROLE }),
            process: computedEntity({ entityType: EntityType.PROCESS }),
            task: computedEntity({ entityType: EntityType.TASK }),
            gateway: computedEntity({ entityType: EntityType.GATEWAY }),
            flow: computedEntity({ entityType: EntityType.FLOW }),
            event: computedEntity({ entityType: EntityType.EVENT }),
            artefact: computedEntity({ entityType: EntityType.ARTEFACT }),
            stek: computedEntity({ entityType: EntityType.STEK }),
            organization: computedEntity({
                entityType: EntityType.ORGANIZATION,
            }),
            isFetching,
        };
    });
};

export const useEntityManager = (entityType: EntityType, entityId: string) =>
    storeToRefs(useEntityStore(entityType, entityId)());

// Helper to get store for current route's entity
export const useCurrentEntityStore = () => {
    return useEntityStore(undefined, undefined, true)();
};

export const useCurrentEntityManager = () =>
    storeToRefs(useCurrentEntityStore());
